import {
  deprecatedDistinctDomainToFieldName,
  deprecatedFormatToSortedDistinctDomains,
} from 'antibodies/AntibodyUtils';
import {LegacyImplementedAntibodyInterface} from 'antibodies/registration-table/LegacyAntibodyRegistrationTypes';
import {assert} from 'Asserts';
import {AntibodyDomainType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  RegistrationTableRowData,
  SchemaInterfaceColumnTransformerBase,
} from 'notebook/integrations/registry/SchemaInterfaceColumnTransformerBase';
import {
  ImplementedStaticSchemaInterfaceGQL,
  AnyImplementedAntibodyInterface,
} from 'notebook/structured-tables/components/modals/RegistrationTableInsertModal';
import {
  AntibodyDomainStructuredTableColumn,
  ComplexPolymerComponentFrontEndMetadata,
  StructuredTableColumn,
  TagValueStructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {TableColumnType} from 'notebook/tables/TableConstants';

function isAntibodyInterface(
  implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL
): implementedSchemaInterface is AnyImplementedAntibodyInterface {
  return implementedSchemaInterface.__typename === 'ImplementedAntibodySchemaInterfaceNode';
}

function isLegacyAntibodyFormat(
  implementedSchemaInterface: AnyImplementedAntibodyInterface
): implementedSchemaInterface is LegacyImplementedAntibodyInterface {
  // eslint-disable-next-line no-restricted-syntax, benchlint/no-in-operator -- TypeScript narrowing
  return 'domains' in implementedSchemaInterface.format;
}

export default class AntibodyRegTableColumnTransformer implements SchemaInterfaceColumnTransformerBase {
  replaceExistingColumn(
    column: StructuredTableColumn,
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL,
    registeredTableRowMetadata: RegistrationTableRowData
  ): StructuredTableColumn | null {
    return null;
  }

  getNewColumns(
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL,
    tableRowData: RegistrationTableRowData
  ): Array<AntibodyDomainStructuredTableColumn> {
    assert(
      isAntibodyInterface(implementedSchemaInterface),
      'Antibody transformer called with non-antibody interface'
    );

    let sortedDomains: Array<{type: AntibodyDomainType; specificity: string | null; label: string}>;

    if (isLegacyAntibodyFormat(implementedSchemaInterface)) {
      const sortedDistinctDomains = deprecatedFormatToSortedDistinctDomains(
        implementedSchemaInterface.format
      );
      sortedDomains = sortedDistinctDomains.map((domain) => ({
        type: domain.type,
        specificity: domain.specificity,
        label: deprecatedDistinctDomainToFieldName(domain),
      }));
    } else {
      sortedDomains = implementedSchemaInterface.format.distinctDomains;
    }

    return sortedDomains.map((domain) => ({
      ...makeStructuredTableColumn({
        columnType: TableColumnType.ANTIBODY_DOMAIN,
        name: domain.label,
        isRequired: true,
        isReadOnly: false,
      }),
      columnType: TableColumnType.ANTIBODY_DOMAIN,
      columnMetadata: {
        domainType: domain.type,
        specificity: domain.specificity,
      },
    }));
  }

  shouldHideColumn(
    column: StructuredTableColumn,
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL
  ): boolean {
    assert(
      isAntibodyInterface(implementedSchemaInterface),
      'Antibody transformer called with non-antibody interface'
    );

    let allDomainNames: Array<string>;
    if (isLegacyAntibodyFormat(implementedSchemaInterface)) {
      allDomainNames = implementedSchemaInterface.format.domains.map((domain) =>
        deprecatedDistinctDomainToFieldName(domain)
      );
    } else {
      allDomainNames = implementedSchemaInterface.format.distinctDomains.map((domain) => domain.label);
    }

    if (column.columnType === TableColumnType.TAG_VALUE) {
      const tagValueColumn = column as TagValueStructuredTableColumn;
      // Hide all computed field columns which match domain names. Ideally, we'd rely on the computed field
      // definition to determine if it's a domain column, but that's not available here. The SSI guarantees
      // that all domain names are reserved for the domain computed fields.
      // See benchling/antibodies/schema/domain/antibody_schema_interface_validator.py
      if (!tagValueColumn.schemaFieldIsComputed) {
        return false;
      }
      return allDomainNames.includes(tagValueColumn.schemaFieldDisplayName ?? '');
    }
    return false;
  }

  getFrontEndMetadata(
    column: StructuredTableColumn,
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL
  ): ComplexPolymerComponentFrontEndMetadata | null {
    // TODO(BNCH-128762) Use this to do the schema field stuff.
    return null;
  }
}
